import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { 
  Shield, Phone, CheckCircle, AlertTriangle, 
  DollarSign, FileText, Users, ArrowRight, Search
} from 'lucide-react';
import { insuranceProviders } from '../data/mock';

const InsurancePage = () => {
  const [searchQuery, setSearchQuery] = useState('');

  const filteredProviders = insuranceProviders.filter(provider =>
    provider.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const insuranceInfo = [
    {
      title: 'Private Insurance',
      providers: ['Aetna', 'Blue Cross Blue Shield', 'Cigna', 'United Healthcare', 'Humana', 'Anthem'],
      coverage: 'Comprehensive coverage for most treatment levels',
      typical: '70-90% coverage after deductible',
      color: 'blue'
    },
    {
      title: 'Medicare',
      providers: ['Medicare Part A', 'Medicare Part B', 'Medicare Advantage'],
      coverage: 'Covers medically necessary addiction treatment',
      typical: '80% coverage for approved services',
      color: 'green'
    },
    {
      title: 'Medicaid',
      providers: ['State Medicaid Programs', 'Medicaid Managed Care'],
      coverage: 'Essential addiction treatment services',
      typical: 'Full coverage for qualifying individuals',
      color: 'purple'
    }
  ];

  const verificationSteps = [
    {
      step: 1,
      title: 'Contact Your Insurance',
      description: 'Call the number on your insurance card to verify addiction treatment benefits.',
      icon: <Phone className="h-6 w-6" />
    },
    {
      step: 2,
      title: 'Ask Key Questions',
      description: 'Inquire about deductibles, copays, and which treatment centers are in-network.',
      icon: <FileText className="h-6 w-6" />
    },
    {
      step: 3,
      title: 'Get Pre-Authorization',
      description: 'Some treatments require prior authorization from your insurance company.',
      icon: <CheckCircle className="h-6 w-6" />
    },
    {
      step: 4,
      title: 'Understand Your Benefits',
      description: 'Know your coverage limits, annual maximums, and out-of-pocket costs.',
      icon: <DollarSign className="h-6 w-6" />
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-600 to-purple-600 text-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Insurance Coverage for Addiction Treatment
            </h1>
            <p className="text-xl text-blue-100 mb-8">
              Understanding your insurance benefits and finding treatment centers that accept your coverage. 
              Don't let insurance concerns prevent you from getting help.
            </p>
            
            <div className="inline-flex items-center bg-white/10 backdrop-blur-sm rounded-full px-6 py-4 mb-8">
              <Shield className="h-5 w-5 mr-3" />
              <span className="mr-2">Free insurance verification:</span>
              <a 
                href="tel:1-888-968-1014"
                className="text-xl font-bold hover:underline"
              >
                1-888-968-1014
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Insurance Types */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Types of Insurance Coverage
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Most insurance plans are required to cover addiction treatment under the Mental Health Parity Act. 
                Here's what different types of insurance typically cover.
              </p>
            </div>

            <div className="grid lg:grid-cols-3 gap-8 mb-12">
              {insuranceInfo.map((info, index) => (
                <Card key={index} className="h-full">
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <Shield className={`h-6 w-6 mr-2 text-${info.color}-600`} />
                      {info.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <h4 className="font-medium mb-2">Common Providers:</h4>
                      <div className="flex flex-wrap gap-1">
                        {info.providers.map((provider) => (
                          <Badge key={provider} variant="outline" className="text-xs">
                            {provider}
                          </Badge>
                        ))}
                      </div>
                    </div>
                    
                    <div>
                      <h4 className="font-medium mb-2">Coverage:</h4>
                      <p className="text-sm text-gray-600">{info.coverage}</p>
                    </div>
                    
                    <div>
                      <h4 className="font-medium mb-2">Typical Coverage:</h4>
                      <p className="text-sm font-medium text-green-600">{info.typical}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Insurance Provider Search */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-center mb-8">
              Find Treatment Centers by Insurance
            </h2>
            
            <Card>
              <CardContent className="p-8">
                <div className="mb-6">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                    <Input
                      type="text"
                      placeholder="Search by insurance provider (e.g., Blue Cross, Aetna, Medicare)..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10 h-12 text-lg"
                    />
                  </div>
                </div>

                {searchQuery ? (
                  <div>
                    <h3 className="font-semibold mb-4">
                      Insurance providers matching "{searchQuery}":
                    </h3>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-2 mb-6">
                      {filteredProviders.map((provider) => (
                        <Badge key={provider} variant="outline" className="p-2 justify-center">
                          {provider}
                        </Badge>
                      ))}
                    </div>
                    {filteredProviders.length === 0 && (
                      <p className="text-gray-600 text-center py-4">
                        No insurance providers found matching your search.
                      </p>
                    )}
                  </div>
                ) : (
                  <div>
                    <h3 className="font-semibold mb-4">Popular Insurance Providers:</h3>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-2 mb-6">
                      {insuranceProviders.slice(0, 12).map((provider) => (
                        <Badge key={provider} variant="outline" className="p-2 justify-center">
                          {provider}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                <div className="text-center">
                  <Button asChild size="lg">
                    <Link to="/search">
                      Find Treatment Centers
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Verification Steps */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl font-bold text-center mb-12">
              How to Verify Your Insurance Benefits
            </h2>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {verificationSteps.map((item) => (
                <Card key={item.step} className="text-center h-full">
                  <CardContent className="pt-8">
                    <div className="inline-flex p-4 rounded-full bg-blue-100 text-blue-600 mb-4">
                      {item.icon}
                    </div>
                    <div className="text-2xl font-bold text-gray-900 mb-2">
                      Step {item.step}
                    </div>
                    <h3 className="font-semibold mb-3">{item.title}</h3>
                    <p className="text-sm text-gray-600">{item.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Key Questions */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-center mb-8">
              Important Questions to Ask Your Insurance
            </h2>
            
            <div className="grid md:grid-cols-2 gap-8">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <FileText className="h-5 w-5 mr-2 text-blue-600" />
                    Coverage Questions
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5" />
                    <span className="text-sm">What is my deductible for mental health/substance abuse treatment?</span>
                  </div>
                  
                  <div className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5" />
                    <span className="text-sm">What are my copay amounts for different levels of care?</span>
                  </div>
                  
                  <div className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5" />
                    <span className="text-sm">How many treatment days are covered per year?</span>
                  </div>
                  
                  <div className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5" />
                    <span className="text-sm">Is there a lifetime maximum for addiction treatment?</span>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Users className="h-5 w-5 mr-2 text-purple-600" />
                    Provider Questions
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5" />
                    <span className="text-sm">Which treatment centers are in my network?</span>
                  </div>
                  
                  <div className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5" />
                    <span className="text-sm">Do I need a referral from my primary care doctor?</span>
                  </div>
                  
                  <div className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5" />
                    <span className="text-sm">Is prior authorization required for treatment?</span>
                  </div>
                  
                  <div className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5" />
                    <span className="text-sm">What's my out-of-network coverage percentage?</span>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* No Insurance Section */}
      <section className="py-16 bg-yellow-50 border-y border-yellow-200">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex p-4 rounded-full bg-yellow-100 text-yellow-600 mb-6">
              <AlertTriangle className="h-8 w-8" />
            </div>
            
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Don't Have Insurance?
            </h2>
            <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
              Don't let lack of insurance prevent you from getting help. Many treatment centers offer 
              payment plans, sliding scale fees, and financial assistance programs.
            </p>
            
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <Card>
                <CardContent className="pt-6 text-center">
                  <DollarSign className="h-8 w-8 text-green-600 mx-auto mb-3" />
                  <h3 className="font-semibold mb-2">Payment Plans</h3>
                  <p className="text-sm text-gray-600">Many facilities offer flexible payment options</p>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="pt-6 text-center">
                  <Users className="h-8 w-8 text-blue-600 mx-auto mb-3" />
                  <h3 className="font-semibold mb-2">Financial Aid</h3>
                  <p className="text-sm text-gray-600">Sliding scale fees based on income</p>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="pt-6 text-center">
                  <Shield className="h-8 w-8 text-purple-600 mx-auto mb-3" />
                  <h3 className="font-semibold mb-2">State Programs</h3>
                  <p className="text-sm text-gray-600">Free or low-cost state-funded treatment</p>
                </CardContent>
              </Card>
            </div>
            
            <Button asChild size="lg">
              <a href="tel:1-888-968-1014">
                <Phone className="h-5 w-5 mr-2" />
                Call for Financial Options: 1-888-968-1014
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-purple-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">
            Get Help With Insurance Verification
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Our treatment coordinators can help verify your insurance benefits and find 
            treatment centers that accept your coverage at no cost to you.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="bg-white text-blue-600 hover:bg-gray-100">
              <a href="tel:1-888-968-1014">
                <Phone className="h-5 w-5 mr-2" />
                Free Insurance Check: 1-888-968-1014
              </a>
            </Button>
            <Button asChild variant="outline" size="lg" className="border-white text-white hover:bg-white hover:text-blue-600">
              <Link to="/get-help">
                Get Help Online
                <ArrowRight className="h-4 w-4 ml-2" />
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default InsurancePage;